import React from "react";
import { Anchor } from "lucide-react";
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Badge } from "@/components/ui/badge";

function RefreshMap({ position }) {
  const map = useMap();

  React.useEffect(() => {
    const timers = [100, 350, 800].map((delay) => setTimeout(() => map.invalidateSize({ pan: false }), delay));
    if (position) setTimeout(() => map.setView(position, 16), 180);
    return () => timers.forEach(clearTimeout);
  }, [map, position]);

  return null;
}

export default function MarinaMapIntelligence({ marina, lat, lng }) {
  const hasPosition = Number.isFinite(lat) && Number.isFinite(lng);
  const position = hasPosition ? [lat, lng] : [38.5, 20.5];

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.06] backdrop-blur-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 p-5">
        <div>
          <h2 className="text-2xl font-black text-white">Interactive Marina Map</h2>
          <p className="mt-1 text-sm text-cyan-50/65">Live chart-style map with marina position and arrival context.</p>
        </div>
        <Badge className="bg-cyan-300 text-slate-950 hover:bg-cyan-300">Real map</Badge>
      </div>

      <div className="relative h-[24rem] overflow-hidden bg-sky-100 sm:h-[30rem]">
        <MapContainer center={position} zoom={hasPosition ? 16 : 5} scrollWheelZoom={false} className="!relative !z-0 !block !h-full !w-full !bg-sky-100" style={{ height: "100%", width: "100%", minHeight: "24rem", background: "#dbeafe" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
            detectRetina
          />
          <RefreshMap position={hasPosition ? position : null} />
          {hasPosition && (
            <CircleMarker
              center={position}
              radius={12}
              pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#1CA7A6", fillOpacity: 1 }}
            >
              <Popup>
                <div className="min-w-44">
                  <p className="font-bold text-slate-900">{marina.name}</p>
                  <p className="text-sm text-slate-600">{marina.location}</p>
                  {marina.vhf_channel && <p className="text-sm font-bold text-slate-700">VHF {marina.vhf_channel}</p>}
                </div>
              </Popup>
            </CircleMarker>
          )}
        </MapContainer>

        {!hasPosition && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center bg-slate-950/70 p-8 text-center text-white">
            <div>
              <Anchor className="mx-auto h-12 w-12 text-cyan-200" />
              <h3 className="mt-4 text-xl font-black">Coordinates unavailable</h3>
              <p className="mt-2 text-sm text-cyan-50/65">Use marina office instructions and official charts for final approach.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}